import React, { useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useSetRecoilState } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { IconClose } from 'components/Icons'
import { useOnClickOutside } from 'helpers/hooks'

import clsx from 'clsx'

import ModalPortal from './ModalPortal'
import ModalPortalPrompt from './ModalPortalPrompt'

export interface Props {
  /**
   * Is this Modal open?
   */
  isOpen: boolean
  /**
   * Is this show icon close?
   */
  isIcon?: boolean
  /**
   * What to do when Modal close function triggered?
   */
  onClose: () => void
  /**
   * Is this Pending modal?
   */
  isPending?: boolean
  /**
   * Content of this Modal?
   */
  children: ReactNode

  isPrompt?: boolean

  isFilledModal?: boolean
}

export const Modal: React.FC<Props> = ({
  children,
  onClose,
  isOpen,
  isIcon = true,
  isPending = false,
  isFilledModal,
}: Props) => {
  const base = 'con-modal'

  const checkPending = !isPending
  const overlayRef = useRef<HTMLDivElement>(null)
  const setIsOpenModal = useSetRecoilState(openModalState)

  const handleClose = (): void => {
    setIsOpenModal(false)
    onClose()
  }

  useOnClickOutside(overlayRef, handleClose)

  useEffect(() => {
    setIsOpenModal(isOpen)
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className={base}>
      <div
        className={clsx(
          {
            [`${base}__modal-card`]: checkPending,
          },
          {
            [`${base}__modal-card__pending`]: isPending,
          },
          {
            [`${base}__modal-card__filled-modal`]: isFilledModal,
          },
        )}
        ref={overlayRef}
      >
        {isIcon && (
          <IconClose
            variants='md'
            className={`${base}__close-btn`}
            onClick={handleClose}
          />
        )}
        {children}
      </div>
    </div>
  )
}

function withPortal(Modal: React.FC<Props>) {
  return function EnhancedComponent(props: Props): JSX.Element {
    return (
      <React.Fragment>
        {props.isPrompt ? (
          <ModalPortalPrompt>
            <Modal {...props} />
          </ModalPortalPrompt>
        ) : (
          <ModalPortal>
            <Modal {...props} />
          </ModalPortal>
        )}
      </React.Fragment>
    )
  }
}

export default withPortal(Modal)
